import Vue from 'vue'
import Vuex from 'vuex'
import VuexPersist from 'vuex-persist'
import localforage from 'localforage'

Vue.use(Vuex)

class HybridStorage {
    constructor(key) {
        this.key = key
        this.migrated = false
    }

    async getItem(key) {
        // Try IndexedDB first
        try {
            let data = await localforage.getItem(key)

            // If not in IndexedDB, check localStorage
            if (!data && !this.migrated) {
                const localData = localStorage.getItem(key)
                if (localData) {
                    data = JSON.parse(localData)
                    // Migrate to IndexedDB
                    await this.setItem(key, data)
                    localStorage.removeItem(key)
                    this.migrated = true
                }
            }

            return data
        } catch (error) {
            console.error('Error reading from storage:', error)
            return null
        }
    }

    async setItem(key, value) {
        try {
            await localforage.setItem(key, value)
        } catch (error) {
            console.error('Error writing to storage:', error)
        }
    }

    async removeItem(key) {
        await localforage.removeItem(key)
    }
}

localforage.config({
    name: 'bzuco',
    storeName: 'vuex',
    driver: localforage.INDEXEDDB
})

const vuexLocalStorage = new VuexPersist({
    key: 'vuex',
    storage: new HybridStorage('vuex'),
    asyncStorage: true

    // storage: window.localStorage, // or window.sessionStorage or localForage
    // Function that passes the state and returns the state with only the objects you want to store.
    // reducer: state => state,
    // Function that passes a mutation and lets you decide if it should update the state in localStorage.
    // filter: mutation => (true)
})

import USER_LOGGED from './graphql/UserLogged.graphql'
import LOGIN from './graphql/Login.graphql'
import LOGOUT from './graphql/Logout.graphql'
import EVENTS from './graphql/Events.graphql'
import CHECKPOINTS from './graphql/Checkpoints.graphql'
import ZONES from './graphql/Zones.graphql'
import TICKETS from './graphql/Tickets.graphql'
import TICKET_USE from './graphql/TicketUse.graphql'
import TICKET_USE_ONLINE from './graphql/TicketUseOnline.graphql'
import TICKET_INFO from './graphql/Info.graphql'
import TICKET_DIRECT_USE_ONLINE from './graphql/TicketDirectUseOnline.graphql'
import TICKETS_UPDATED from './graphql/TicketsUpdated.graphql'
import TICKETS_RESET from './graphql/TicketsReset.graphql'
import TICKET_ONLINE from './graphql/TicketOnline.graphql'
import ZONE_STATS from './graphql/ZoneStats.graphql'

const initialState = {
    config: {
        server: process.env.VUE_APP_ENV === 'dev' ? 'http://localhost:8000/': '/',
        assetsDir: '/static/bzuco_guard/mobile/',
        env: process.env.VUE_APP_ENV
    },
    user: null,

    events: [],
    checkpoints: [],
    zones: [],

    activeEvents: [],
    activeCheckpoint: null,
    activeZones: [],
    zoneStats: [],

    tickets: [],
    lastCode: '',
    messages: [],
    lastZones: [],
    lastZoneLoadDate: null,

    updateTimer: null,
    networkError: false,

    // failedCodeUse: [],
    sendQueue: [],
    sendQueueSending: false,

    settings: {
        sound: true,
        camera: '',
        nfc: false,
        lang: 'cs',
        videoHeight: 300,
        group: '',
        olderEvents: 30,

        patchSize: 60,
        halfSample: true,
        numOfWorkers: 2,
        frequency: 10,
        showTicketCount: true
    }
}

export default new Vuex.Store({
    plugins: [vuexLocalStorage.plugin],
    state: initialState,
    mutations: {
        reset(state) {
            const initialCopy = JSON.parse(JSON.stringify(initialState))
            Object.keys(initialCopy).forEach(key => {
                state[key] = initialCopy[key]
            })
        },
        setEvents(state, events = null) {
            events.forEach(event => {
                event.ticketsets.forEach(ticketset => {
                    ticketset.selected = false
                })
            })
            state.events = events
        },
        setCheckpoints(state, checkpoints = null) {
            state.checkpoints = checkpoints
        },
        setZones(state, zones = null) {
            state.zones = zones
        },
        setUser(state, user = null) {
            state.user = user
        },
        setActiveEvents(state, events) {
            state.activeEvents = events
        },
        setActiveCheckpoint(state, id) {
            state.activeCheckpoint = id
        },
        setActiveZones(state, ids) {
            state.activeZones = ids
        },
        clearTickets(state) {
            state.tickets = []
        },
        setTickets(state, {id, list}) {
            state.tickets = [...state.tickets, {
                id: id,
                last_updated_at: list.reduce((acc, ticket) => {
                    const pt = new Date(ticket.updatedAt)
                    if (pt > acc) {
                        return pt
                    }
                    return acc
                }, 0),
                list: list
            }]
        },
        updateTickets(state, {id, list}) {
            state.tickets.forEach(ticketset => {
                if (ticketset.id === id) {
                    list.forEach(item => {
                        let ticket = ticketset.list.find(ticket => ticket.id === item.id)
                        if (!ticket) {
                            ticket = {...item}
                            ticketset.list.push(ticket)
                        } else {
                            Object.keys(item).forEach(key => ticket[key] = item[key])
                        }
                    })

                    if (list.length) {
                        ticketset.last_updated_at = ticketset.list.reduce((acc, ticket) => {
                            const pt = new Date(ticket.updatedAt)
                            if (pt > acc) {
                                return pt
                            }
                            return acc
                        }, 0)
                    }
                }
            })
        },
        setLastCode(state, lastCode) {
            state.lastCode = lastCode
        },
        useCode(state, data) {
            state.tickets.forEach(ticketset => {
                if (data.ticketsetId == ticketset.id) {
                    const ticket = ticketset.list.find(ticket => ticket.code === data.code)
                    if (ticket) {
                        ticket.used = true
                        ticket.used_at = Date.now()
                    }
                }
            })
        },
        clearMessages(state) {
            state.messages = []
        },
        addMessage(state, message) {
            state.messages.unshift(message)
            state.messages = state.messages.slice(0,50)
        },
        setUpdateTimer(state, value) {
            state.updateTimer = value
        },
        showNetworkError(state) {
            state.networkError = true
        },

        // addFailedCodeUse(state, data) {
        //     if (!state.failedCodeUse.find(b => b.code === data.code)) {
        //         state.failedCodeUse.push(data)
        //     }
        // },
        // removeFailedCodeUse(state, code) {
        //     if (state.failedCodeUse.includes(code)) {
        //         state.failedCodeUse = [...state.failedCodeUse.filter(c => c !== code)]
        //     }
        // },

        addToSendQueue(state, data) {
            if (!state.sendQueue.find(b => b.code === data.code && b.ticketsetId === data.ticketsetId)) {
                state.sendQueue.push(data)
            }
        },
        removeFromSendQueue(state, data) {
            console.log(['removeFromSendQueue', data])

            state.messages
                .filter(m => m.code === data.code && m.ticketsetId === data.ticketsetId && m.type === 'success')
                .forEach(message => {
                    message.sent = true
                })

            state.messages = [...state.messages]

            if (state.sendQueue.find(b => b.code === data.code && b.ticketsetId === data.ticketsetId)) {
                state.sendQueue = [...state.sendQueue.filter(c => {
                    return !(c.code === data.code && c.ticketsetId === data.ticketsetId)
                })]
            }
        },

        setSettingsCamera(state, deviceId) {
            state.settings.camera = deviceId
        },
        setSettingsGroup(state, value) {
            state.settings.group = value
        },
        setSettingsOlderEvents(state, value) {
            state.settings.olderEvents = value
        },
        setSettingsSound(state, sound) {
            state.settings.sound = sound
        },
        setSettingsLang(state, lang) {
            state.settings.lang = lang
        },
        setSettingsVideoHeight(state, height) {
            state.settings.videoHeight = height
        },

        setSettingsPatchSize(state, value) {
            state.settings.patchSize = value
        },
        setSettingsHalfSample(state, value) {
            state.settings.halfSample = value
        },
        setSettingsNumOfWorkers(state, value) {
            state.settings.numOfWorkers = value
        },
        setSettingsFrequency(state, value) {
            state.settings.frequency = value
        },
        setSettingsShowTicketCount(state, value) {
            state.settings.showTicketCount = value
        },
        setSending(state, value) {
            state.sendQueueSending = value
        },
        setLastZones(state, value) {
            state.lastZones = value
        },
        setLastZoneLoadDate(state, value) {
            state.lastZoneLoadDate = value
        },
        setZoneStats(state, value) {
            state.zoneStats = value
        }
    },
    getters: {
        getTicketByCode: (state) => (code) => {
            let ret = [null, null]
            state.tickets.forEach(ticketset => {
                const t = ticketset.list.find(ticket => {
                    const shortLen = Math.min(ticket.code.length, code.length)
                    const tc = ticket.code.substr(0, shortLen)
                    const cc = code.substr(0, shortLen)
                    return tc === cc
                })
                if (t) {
                    ret = [t, ticketset]
                }
            })
            return ret
        },
        eventById: (state) => (id) => {
            return state.events.find(event => event.id === id)
        },
        activeTicketsetById: (state, getters) => (id) => {
            let ret = null

            if (state.activeZones) {
                state.events.forEach(event => {
                    event.ticketsets.forEach(ticketset => {
                        if (ticketset.id == id) {
                            ret = ticketset
                        }
                    })
                })

            } else if (state.activeCheckpoint) {
                getters.activeCheckpointObject.ticketsets.forEach(ticketset => {
                    if (ticketset.id == id) {
                        ret = ticketset
                    }
                })

            } else {
                state.activeEvents.forEach(eventId => {
                    const event = getters.eventById(eventId)
                    event.ticketsets.forEach(ticketset => {
                        if (ticketset.id == id) {
                            ret = ticketset
                        }
                    })
                })
            }
            return ret
        },
        activeEventByTicketsetId: (state, getters) => (id) => {
            if (state.activeCheckpoint) {
                return null
            }

            let ret = null
            state.activeEvents.forEach(eventId => {
                const event = getters.eventById(eventId)
                event.ticketsets.forEach(ticketset => {
                    if (ticketset.id == id) {
                        ret = event
                    }
                })
            })
            return ret
        },
        activeCheckpointObject: (state) => {
            return state.checkpoints.find(checkpoint => checkpoint.id === state.activeCheckpoint)
        }
    },
    actions: {
        getUserStatus({commit}) {
            return this.$apollo.query({
                query: USER_LOGGED,
                fetchPolicy: 'no-cache'
            }).then((result) => {
                if (result.data.mobile.userLogged) {
                    commit('setUser', result.data.mobile.userLogged.device)
                    return Promise.resolve(result.data.mobile.userLogged.device)
                } else {
                    commit('setUser', null)
                    return Promise.resolve(null)
                }
            })
        },

        loginUser({commit}, data) {
            return this.$apollo.mutate({
                mutation: LOGIN,
                variables: data
            }).then((result) => {
                if (result && result.data.mobile.login.ok) {
                    commit('setUser', result.data.mobile.login.device)
                }
                return Promise.resolve(result.data.mobile.login)
            }).catch(error => {
                commit('showNetworkError')
                return Promise.reject(error)
            })
        },

        logoutUser({commit}) {
            return this.$apollo.mutate({
                mutation: LOGOUT,
            }).then((result) => {
                if (result && result.data.mobile.logout.ok) {
                    commit('setUser', null)
                }
                return Promise.resolve(result.data.mobile.logout)
            }).catch(error => {
                commit('showNetworkError')
                return Promise.reject(error)
            })
        },

        loadEvents({commit}, days = 0) {
            const dayMs = 86400000 //number of milliseconds in a day
            let fromDate = new Date(new Date().getTime() - days * dayMs)

            return this.$apollo.query({
                query: EVENTS,
                variables: { fromDate: fromDate.toISOString().substr(0, 19) },
                fetchPolicy: 'no-cache'
            }).then((result) => {
                commit('setEvents', result.data.mobile.events)
                return Promise.resolve(result.data.mobile.events)
            }).catch(error => {
                commit('showNetworkError')
                return Promise.reject(error)
            })
        },

        loadCheckpoints({commit}) {
            return this.$apollo.query({
                query: CHECKPOINTS,
                fetchPolicy: 'no-cache'
            }).then((result) => {
                let checkpoints = result.data.mobile.checkpoints
                checkpoints.forEach(checkpoint => {
                    checkpoint.ticketsets.forEach(ticketset => {
                        ticketset.selected = false
                    })
                })
                commit('setCheckpoints', checkpoints)
                return Promise.resolve(checkpoints)
            }).catch(error => {
                commit('showNetworkError')
                return Promise.reject(error)
            })
        },

        loadZones({commit}) {
            return this.$apollo.query({
                query: ZONES,
                fetchPolicy: 'no-cache'
            }).then((result) => {
                let zones = result.data.mobile.zones
                zones.forEach(zone => {
                    zone.selected = false
                })
                commit('setZones', zones)
                return Promise.resolve(zones)
            }).catch(error => {
                commit('showNetworkError')
                return Promise.reject(error)
            })
        },

        loadTickets({commit}, ticketsetId) {
            return this.$apollo.query({
                query: TICKETS,
                variables: {ticketsetIds:ticketsetId},
                fetchPolicy: 'no-cache'
            }).then((result) => {
                result.data.mobile.tickets.forEach(ticketset => {
                    commit('setTickets', {id:ticketset.ticketsetId, list:ticketset.tickets})
                })
                return Promise.resolve(result.data.mobile.tickets)
            }).catch(error => {
                commit('showNetworkError')
                return Promise.reject(error)
            })
        },

        loadTicketsZones({commit}) {
            console.log('this.activeZones', this.state.activeZones)
            return this.$apollo.query({
                query: TICKETS,
                variables: {zoneIds: this.state.activeZones},
                fetchPolicy: 'no-cache'
            }).then((result) => {
                result.data.mobile.tickets.forEach(ticketset => {
                    commit('setTickets', {id:ticketset.ticketsetId, list:ticketset.tickets})
                })
                return Promise.resolve(result.data.mobile.tickets)
            }).catch(error => {
                commit('showNetworkError')
                return Promise.reject(error)
            })
        },

        ticketUse({commit, dispatch}, data) {
            if (data.isOnline) {
                return this.$apollo.mutate({
                    mutation: TICKET_USE_ONLINE,
                    variables: data
                }).then((result) => {
                    return Promise.resolve(result.data.mobile.ticketUseOnline)
                }).catch(error => {
                    commit('addFailedCodeUse', data)
                    return Promise.reject(error)
                })
            } else {
                commit('addToSendQueue', data)
                dispatch('sendFromQueue')
                return Promise.resolve(false)
            }
        },

        ticketInfo({commit, dispatch}, code) {
            return this.$apollo.mutate({
                mutation: TICKET_INFO,
                variables: {
                    code: code
                }
            }).then((result) => {
                return Promise.resolve(result.data.mobile.info)
            }).catch(error => {
                commit('addFailedCodeUse', code)
                return Promise.reject(error)
            })
        },

        ticketDirectUse({commit, dispatch}, data) {
            return this.$apollo.mutate({
                mutation: TICKET_DIRECT_USE_ONLINE,
                variables: data
            }).then((result) => {
                return Promise.resolve(result.data.mobile.ticketDirectUseOnline)
            }).catch(error => {
                commit('addFailedCodeUse', data)
                return Promise.reject(error)
            })
        },

        sendTicketUse({commit}, data) {
            return this.$apollo.mutate({
                mutation: TICKET_USE,
                variables: data
            }).then((result) => {
                console.log(['queue', result.data.mobile.ticketUse])
                if (result.data.mobile.ticketUse.ok) {
                    commit('useCode', data)
                }
                return Promise.resolve(result.data.mobile.ticketUse)
            }).catch(error => {
                return Promise.reject(error)
            })
        },

        sendFromQueue({commit, dispatch, state}) {
            // console.log(['sendFromQueue', state.sendQueue.length])
            if (state.sendQueue.length > 0 && !state.sendQueueSending) {
                commit('setSending', true)

                const data = state.sendQueue[0]
                dispatch('sendTicketUse', data)
                    .then(result => {
                        commit('setSending', false)
                        if (result.ok) {
                            commit('removeFromSendQueue', data)
                            dispatch('sendFromQueue')
                        } else {
                            console.log('ERROR')
                            commit('removeFromSendQueue', data)
                            commit('addMessage', {
                                type: 'not-found',
                                message: `CHYBA ODESILANI ${data.code} - ${result.message}`
                            })
                        }
                    }).catch((/*error*/) => {
                        commit('setSending', false)
                    })
            }
        },

        ticketOnline({commit}, {code, ticketsetId, checkpoint, zoneIds}) {
            return this.$apollo.mutate({
                mutation: TICKET_ONLINE,
                variables: { code, ticketsetId, checkpoint, zoneIds }
            }).then((result) => {
                return Promise.resolve(result.data.mobile.online)
            }).catch(error => {
                commit('showNetworkError')
                return Promise.reject(error)
            })
        },

        ticketsReset({commit}, ticketsetId) {
            return this.$apollo.mutate({
                mutation: TICKETS_RESET,
                variables: { ticketsetId: ticketsetId }
            }).then((/*result*/) => {
                return Promise.resolve()
            }).catch(error => {
                commit('showNetworkError')
                return Promise.reject(error)
            })
        },

        loadZoneStats({commit}, day) {
            return this.$apollo.query({
                query: ZONE_STATS,
                variables: { day: day },
                fetchPolicy: 'no-cache'
            }).then((result) => {
                commit('setZoneStats', result.data.mobile.zoneStats)
                return Promise.resolve()
            }).catch(error => {
                commit('showNetworkError')
                return Promise.reject(error)
            })
        },

        updateLoopStart({commit}) {
            clearTimeout(this.state.updateTimer)
            const timer = setInterval(() => {
                const ticketsets = this.state.tickets.map(ticketset => {
                    return JSON.stringify({
                        id: ticketset.id,
                        last: ticketset.last_updated_at
                    })
                })

                this.$apollo.query({
                    query: TICKETS_UPDATED,
                    variables: {
                        ticketsets:ticketsets,
                        zoneIds: this.state.activeZones
                    },
                    fetchPolicy: 'no-cache'
                }).then((result) => {
                    result.data.mobile.ticketsUpdated.forEach(ticketset => {
                        commit('updateTickets', {id:ticketset.ticketsetId, list:ticketset.tickets})
                    })
                }).catch((/* error */) => {
                    // ignore errors
                })
            }, 10000)
            commit('setUpdateTimer', timer)
        },

        updateLoopStop({commit}) {
            clearTimeout(this.state.updateTimer)
            commit('setUpdateTimer', null)
        }
    }
})
